import axios from 'axios';

const baseURL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3100';
const uploadBaseURL = process.env.REACT_APP_UPLOAD_URL || baseURL;

const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_info');
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username: string, password: string) =>
    api.post('/api/auth/login', { username, password }),

  register: (username: string, password: string) =>
    api.post('/api/auth/register', { username, password }),

  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_info');
    return Promise.resolve();
  },
};

const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB — small chunks to avoid CDN gateway timeouts

export const ANON_MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB
export const REGISTERED_MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

function generateFileId(): string {
  const timestamp = Date.now().toString();
  const randomChars = Math.random().toString(36).substring(2, 6);
  return `${timestamp}${randomChars}`;
}

export interface UploadProgress {
  percentage: number;
  bytesUploaded: number;
  totalBytes: number;
  speed: number; // bytes per second
}

export interface UploadOptions {
  expiryTime?: string;
  oneTimeDownload?: boolean;
  onProgress?: (progress: UploadProgress) => void;
}

export const uploadAPI = {
  uploadFile: async (file: File, options: UploadOptions = {}) => {
    const fileId = generateFileId();
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const { expiryTime = '1d', oneTimeDownload = false, onProgress } = options;
    const totalBytes = file.size;

    // Track bytes uploaded across all chunks
    const chunkBytesUploaded: number[] = new Array(totalChunks).fill(0);
    const startTime = Date.now();

    const reportProgress = () => {
      if (!onProgress) return;
      const bytesUploaded = chunkBytesUploaded.reduce((a, b) => a + b, 0);
      const elapsed = (Date.now() - startTime) / 1000;
      const speed = elapsed > 0 ? bytesUploaded / elapsed : 0;
      const percentage = totalBytes > 0 ? Math.min(Math.round((bytesUploaded / totalBytes) * 100), 99) : 0;
      onProgress({ percentage, bytesUploaded, totalBytes, speed });
    };

    const uploadChunk = async (chunkIndex: number): Promise<any> => {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const response = await axios.post(`${uploadBaseURL}/upload`, chunk, {
        headers: {
          'Content-Type': 'application/octet-stream',
          'x-file-id': fileId,
          'x-filename': encodeURIComponent(file.name),
          'x-total-chunks': totalChunks.toString(),
          'x-chunk-index': chunkIndex.toString(),
          'x-file-expiry': expiryTime,
          'x-one-time-download': oneTimeDownload.toString(),
          'x-file-size': totalBytes.toString(),
          ...(localStorage.getItem('auth_token')
            ? { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
            : {}),
        },
        timeout: 120000, // 2 min per chunk
        onUploadProgress: (progressEvent) => {
          chunkBytesUploaded[chunkIndex] = progressEvent.loaded;
          reportProgress();
        },
      });

      chunkBytesUploaded[chunkIndex] = end - start;
      reportProgress();
      return response;
    };

    // Upload chunks sequentially for single-file upload
    let lastResponse: any = null;
    for (let i = 0; i < totalChunks; i++) {
      lastResponse = await uploadChunk(i);
    }

    // Final 100%
    if (onProgress) {
      const elapsed = (Date.now() - startTime) / 1000;
      onProgress({ percentage: 100, bytesUploaded: totalBytes, totalBytes, speed: elapsed > 0 ? totalBytes / elapsed : 0 });
    }

    return lastResponse;
  },
};

export const filesAPI = {
  getFiles: () => api.get('/files'),
  getFileDetails: (fileId: string) => api.get(`/files/${fileId}`),
  deleteFile: (fileId: string) => api.delete(`/files/${fileId}`),
};

export const downloadAPI = {
  generateLink: (fileId: string) =>
    api.post('/download/generate-link', { fileId }),
};

export default api;
